import { Controller } from "@hotwired/stimulus"
import { isAbortError } from "../utils/fetch-error.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`. Only the
// contact modal sends these — every other mount of this controller shows no
// offer notice and no source tooltip, so the empty strings stand in.
const DEFAULT_TRANSLATIONS = {
  offerTitle: "",
  sourceNameText: "",
  sourceDomainText: "",
  sourceUnknownText: ""
}

export default class extends Controller {
  static targets = [
    "image",
    "loading",
    "fallback",
    "logoUrl",
    "removeButton",
    "removedNotice",
    "removedHint",
    "removedFlag",
    "website",
    "acceptedFlag",
    "declinedDomain",
    "offerNotice",
    "offerImage",
    "offerTitle",
    "fallbackIcon"
  ]
  static values = {
    // Carries offerTitle plus the three source-tooltip strings. The ✕'s tooltip
    // states where the logo came from, and the server can only render the
    // PERSISTED origin — which is nil on a party that has never been saved. So a
    // live preview showed "Pôvod loga nie je známy" over a logo whose origin we
    // knew perfectly well: the user had just typed the website it came from.
    // sourceNameText / sourceDomainText / sourceUnknownText carry the same three
    // strings the server-side helper picks from
    // (contacts_helper#contact_logo_source_tooltip), so client and server agree.
    translations: { type: Object, default: {} },
    companyName: String,
    domain: String,
    size: { type: Number, default: 64 },
    format: { type: String, default: "png" },
    theme: { type: String, default: "auto" }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    // Every lookup is issued for one company name / domain. Its answer may only
    // be applied while that is still what the form asks for — otherwise a slow
    // Logo.dev response overwrites the logo of a company the user has already
    // moved on from, and Parties::LogoDecision queues it onto the party they
    // renamed to. Same request-identity contract as legal_entity_controller.
    this.requestSequence = 0
    this.abortController = null

    // Check if there's already an existing logo with a valid src
    // Check if src has a non-empty value, not just if the attribute exists
    if (this.hasImageTarget && this.imageTarget.src && this.imageTarget.src.trim() !== "") {
      if (this.imageTarget.classList.contains("d-none")) {
        // Logo is attached but hidden - show it and hide loader
        this.showExistingLogo()
      } else {
        // Logo is already displayed, store its URL
        if (this.hasLogoUrlTarget) {
          this.logoUrlTarget.value = this.imageTarget.src
        }
      }
      return
    }

    // If no existing logo, try to load one if we have company name or domain
    if (this.hasCompanyNameValue || this.hasDomainValue) {
      this.loadLogo()
    } else {
      // No company name or domain available, show fallback
      this.showFallback()
    }
  }

  disconnect() {
    this.#invalidatePendingLookup()
  }

  // Action to refresh logo
  refresh() {
    this.loadLogo()
  }

  // Action to load logo for company name input
  // A private person's name is a PERSON's name. Guessing a brand logo from it is
  // wrong at best — Logo.dev matches a real company that happens to share the
  // name — and it silently attached that company's mark to a named individual.
  // A person can still get a logo from a website they enter (loadForWebAddress),
  // which is a deliberate act with a real source behind it.
  loadForCompanyName(event) {
    if (this.#isPhysicalPerson()) {
      this.showFallback()
      return
    }

    const companyName = event.target.value
    if (companyName && companyName.trim().length > 2) {
      this.companyNameValue = companyName
      this.loadLogo()
    } else if (!companyName || companyName.trim().length === 0) {
      this.showFallback()
    }
  }

  // Swaps the no-name-yet glyph when the entity-type radios change, and drops a
  // name-guessed logo that is no longer appropriate. Wired from the radios.
  setEntityType() {
    const person = this.#isPhysicalPerson()
    if (this.hasFallbackIconTarget) this.fallbackIconTarget.textContent = person ? "person" : "apartment"
    if (person && this.logoSourceIsNameGuess()) this.showFallback()
  }

  logoSourceIsNameGuess() {
    return this.hasRemoveButtonTarget && this.removeButtonTarget.classList.contains("header__icon-remove--persistent")
  }

  #isPhysicalPerson() {
    const radio = document.querySelector("input[name='entity_type'][value='physical_person']")
    return Boolean(radio?.checked)
  }

  // Action to load logo for web address
  loadForWebAddress(event) {
    // The website IS the rejection key's input, so a banner already on screen
    // goes stale the moment this field changes.
    this.#updateRemovedHint()
    const webAddress = event.target.value
    if (webAddress && webAddress.trim().length > 0) {
      const domain = this.#canonicalDomain(webAddress)
      if (domain) {
        this.domainValue = domain
        // A staged (this session) or persisted (already saved) rejection means the
        // user has already refused a logo — offer this one rather than silently
        // swapping it in, so a refusal doesn't read as being ignored.
        if (this.#removed()) {
          // ...unless this is the very domain they already refused. Offering that
          // one back reads as ignoring the refusal just as much as auto-applying
          // it would (FR-34/AC-6).
          if (!this.#alreadyRefused(domain)) this.#fetchOffer(domain)
        } else {
          this.loadLogo()
        }
      }
    } else {
      // If web address is empty, show fallback
      this.showFallback()
    }
  }

  // Staged locally and persisted on Save, like every other field in the modal.
  remove() {
    this.removedSrc = this.hasImageTarget ? this.imageTarget.src : ""
    this.showFallback()

    if (this.hasRemovedFlagTarget) this.removedFlagTarget.value = "1"
    if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.add("d-none")
    this.#updateRemovedHint()
    if (this.hasRemovedNoticeTarget) this.removedNoticeTarget.classList.remove("d-none")
  }

  undoRemove() {
    if (this.hasRemovedFlagTarget) this.removedFlagTarget.value = "0"
    if (this.hasRemovedNoticeTarget) this.removedNoticeTarget.classList.add("d-none")
    if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.remove("d-none")
    // "restored", not "name": undoing a removal puts back whatever was already
    // there, so it must not be re-labelled a fresh name guess and given the
    // persistent treatment the server did not render it with.
    if (this.removedSrc) this.displayLogo(this.removedSrc, { source: "restored" })
  }

  // Action to reset if field is empty
  resetIfEmpty(event) {
    const value = event.target.value
    if (!value || value.trim().length === 0) {
      this.reset()
    }
  }

  // Reset method to show fallback and clear values
  reset() {
    this.companyNameValue = ""
    this.domainValue = ""
    this.showFallback()
    // Clear the logo URL when resetting
    if (this.hasLogoUrlTarget) {
      this.logoUrlTarget.value = ""
    }
  }

  // ===== PUBLIC METHODS =====

  // The user just rejected a logo. Silently substituting another reads as
  // being ignored — even though a domain IS a better source than the name
  // guess they refused. So we offer it and let them decide.
  displayOffer(logoUrl, domain) {
    if (!this.hasOfferNoticeTarget) return

    this.offeredUrl = logoUrl
    this.offeredDomain = domain
    if (this.hasOfferImageTarget) this.offerImageTarget.src = logoUrl
    if (this.hasOfferTitleTarget) {
      this.offerTitleTarget.textContent = this.#texts.offerTitle.replace("%{domain}", domain)
    }
    this.offerNoticeTarget.classList.remove("d-none")
  }

  acceptOffer() {
    if (this.hasRemovedFlagTarget) this.removedFlagTarget.value = "0"
    if (this.hasRemovedNoticeTarget) this.removedNoticeTarget.classList.add("d-none")
    // remove() hid the ✕; a freshly accepted logo is exactly the thing that may
    // still turn out wrong, so give the control back rather than making the user
    // save and reopen to get rid of it.
    if (this.hasRemoveButtonTarget) this.removeButtonTarget.classList.remove("d-none")
    if (this.offeredUrl) this.displayLogo(this.offeredUrl, { accepted: true, source: "domain" })
    // Not declineOffer(): accepting is the opposite decision and must not record
    // this domain as refused.
    this.#hideOffer()
  }

  // A decline is a DECISION and has to survive the save. Hiding the notice and
  // submitting nothing left the old key in place, and find_logo_url_for blocks a
  // domain only on an exact key match — so the refused logo was attached by the
  // next FetchLogoJob (FR-34/AC-6).
  declineOffer() {
    if (this.hasDeclinedDomainTarget && this.offeredDomain) {
      this.declinedDomainTarget.value = this.offeredDomain
    }
    this.#hideOffer()
  }

  hasExistingLogo() {
    return (
      this.hasImageTarget &&
      this.imageTarget.src &&
      this.imageTarget.src.trim() !== "" &&
      !this.imageTarget.classList.contains("d-none")
    )
  }

  async loadLogo() {
    // Every entry point funnels through here — connect's auto-load, refresh, and
    // a company-name edit — so this is where a staged or persisted removal has to
    // be honoured. Only loadForWebAddress used to check, so editing the company
    // name after clicking ✕ put a fresh guess back in the header while the
    // removal banner was still showing, with no ✕ left to remove it, and the
    // server discarded it on Save.
    if (this.#removed()) return

    const request = this.#beginLookup()

    // Hide both image and placeholder during loading
    this.hideAll()

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("d-none")
    }

    try {
      const response = await fetch(request.url, { signal: this.abortController.signal })
      if (!this.#isCurrentLookup(request)) return

      if (response.ok) {
        const data = await response.json()
        if (!this.#isCurrentLookup(request)) return

        if (data.logo_url) {
          // Say WHICH lookup answered. buildLogoUrl hits the domain endpoint
          // whenever there is a domain and only falls back to the name, so the
          // same test decides the origin. Without this every logo defaulted to
          // "name" and one typed from a website was labelled a guess — or, when
          // the strings had not loaded, "origin unknown" over a logo whose
          // origin the user had just supplied.
          this.displayLogo(data.logo_url, { source: this.hasDomainValue ? "domain" : "name" })
        } else {
          this.showFallback()
        }
      } else if (response.status === 404) {
        // 404 is expected when no logo is found - treat as normal case
        this.showFallback()
      } else {
        // Other HTTP errors - still show fallback but could log if needed
        this.showFallback()
      }
    } catch (error) {
      // Network errors or other exceptions. An abort is a newer lookup
      // superseding this one, never a failure — falling back here would clear
      // the logo the newer lookup is still fetching.
      if (isAbortError(error)) return
      if (!this.#isCurrentLookup(request)) return

      this.showFallback()
    } finally {
      // Guarded like every other post-await mutation: a superseded lookup must
      // not hide the spinner belonging to the one still in flight.
      if (this.#isCurrentLookup(request) && this.hasLoadingTarget) {
        this.loadingTarget.classList.add("d-none")
      }
    }
  }

  // Supersedes whatever lookup is in flight and returns the identity of the new
  // one. Mirrors legal_entity_controller's requestSequence/#isCurrentRequest
  // pair — the same defect, fixed the same way.
  #beginLookup() {
    this.#invalidatePendingLookup()
    this.abortController = new AbortController()

    return { id: this.requestSequence, url: this.buildLogoUrl() }
  }

  // Cancels the lookup in flight and makes any response still on the wire
  // stale, so it can never be displayed or written to the hidden logo_url.
  #invalidatePendingLookup() {
    this.abortController?.abort()
    this.abortController = null
    this.requestSequence += 1
  }

  // A response may only be applied while it is still the newest lookup AND the
  // form still asks for the same company name / domain — reset() and
  // loadForWebAddress move those values without necessarily issuing a lookup of
  // their own.
  #isCurrentLookup(request) {
    return request.id === this.requestSequence && request.url === this.buildLogoUrl()
  }

  buildLogoUrl() {
    const params = new URLSearchParams({
      size: this.sizeValue,
      format: this.formatValue,
      theme: this.themeValue
    })

    let url
    if (this.hasDomainValue) {
      url = `/logos/domain/${encodeURIComponent(this.domainValue)}?${params}`
    } else if (this.hasCompanyNameValue) {
      url = `/logos/company/${encodeURIComponent(this.companyNameValue)}?${params}`
    } else {
      url = `/logos/search?q=${encodeURIComponent(this.companyNameValue)}&${params}`
    }

    return url
  }

  // `accepted` marks the URL as an explicit user pick (the FR-34 offer), which
  // is what the server needs to tell a deliberate choice apart from the preview
  // simply re-rendering after a name edit. Every other caller resets it, so a
  // later re-render can never inherit someone else's acceptance.
  displayLogo(logoUrl, { accepted = false, source = "name" } = {}) {
    if (this.#removed() && !accepted) return

    if (this.hasImageTarget) {
      this.imageTarget.src = logoUrl
      this.imageTarget.classList.remove("d-none")
    }

    // A displayed logo is always refusable — that is the whole point of rendering
    // the control whenever the modal renders (see _contact_modal_content.html.erb),
    // so an unwanted guess can be refused BEFORE it is ever attached rather than
    // only after a save. Only undoRemove and acceptOffer used to reveal it, so the
    // one path that most needs it — a live Logo.dev preview on a logo-less party,
    // the least trustworthy source there is — showed a logo with no way to say no.
    // A name guess keeps the control permanently visible; a more exact source only
    // reveals it on hover/focus, matching how the server renders each variant.
    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.classList.remove("d-none")
      this.removeButtonTarget.classList.toggle("header__icon-remove--persistent", source === "name")
      this.#setSourceTooltip(source)
    }

    if (this.hasFallbackTarget) {
      this.fallbackTarget.classList.add("d-none")
    }

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("d-none")
    }

    // Store the logo URL in the hidden field for form submission
    if (this.hasLogoUrlTarget) {
      this.logoUrlTarget.value = logoUrl
    }
    this.#setAccepted(accepted)
  }

  // Hide both image and fallback during loading
  hideAll() {
    if (this.hasImageTarget) {
      this.imageTarget.classList.add("d-none")
    }

    if (this.hasFallbackTarget) {
      this.fallbackTarget.classList.add("d-none")
    }

    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("d-none")
    }
  }

  // Show fallback (placeholder or default icon)
  showFallback() {
    this.hideAll()
    if (this.hasFallbackTarget) {
      this.fallbackTarget.classList.remove("d-none")
    }
    // Clear the logo URL when showing fallback
    if (this.hasLogoUrlTarget) {
      this.logoUrlTarget.value = ""
    }
    this.#setAccepted(false)
  }

  showExistingLogo() {
    if (this.hasImageTarget) {
      this.imageTarget.classList.remove("d-none")
    }
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("d-none")
    }
    if (this.hasFallbackTarget) {
      this.fallbackTarget.classList.add("d-none")
    }
    if (this.hasLogoUrlTarget) {
      this.logoUrlTarget.value = this.imageTarget.src
    }
    this.#setAccepted(false)
  }

  // Mirrors contacts_helper#contact_logo_source_tooltip, including its fallback:
  // "domain" with no resolvable domain is still unknown, not a half-written
  // sentence.
  //
  // Only the tooltip changes. The aria-label stays "remove logo" — it is the
  // button's accessible NAME, and replacing it with the origin would leave a
  // screen-reader user with a control that never says what it does.
  #setSourceTooltip(source) {
    const domain = this.#canonicalDomain(this.domainValue) || ""
    let title = this.#texts.sourceUnknownText

    if (source === "name") {
      title = this.#texts.sourceNameText
    } else if (source === "domain" && domain) {
      title = this.#texts.sourceDomainText.replace("%{domain}", domain)
    }
    if (!title) return

    this.removeButtonTarget.setAttribute("data-tooltip-title-value", title)
  }

  #removed() {
    return this.hasRemovedFlagTarget && this.removedFlagTarget.value === "1"
  }

  // The banner has to name the decision the SERVER will record, and the server
  // records it from the website being SUBMITTED — Identity::PartyEditor writes
  // the new website before Parties::LogoDecision#reject reads it — not from the
  // one this text was rendered with. Typing a website and then removing the
  // logo therefore promised only "we won't guess from the company name again"
  // while saving "domain:<host>", a strictly stronger rejection that blocks the
  // name guess too, about a decision the user cannot otherwise see.
  //
  // Mirrors Party.logo_rejection_key_for exactly: a domain key wins only when
  // there IS a domain and the current source is "domain" or unknown. A name
  // guess or an uploaded logo is always rejected by the bare "name" key,
  // whatever the website says.
  #updateRemovedHint() {
    if (!this.hasRemovedHintTarget) return

    const { hintName = "", hintDomain = "", logoSource = "" } = this.removedHintTarget.dataset
    const domain = this.hasWebsiteTarget ? this.#canonicalDomain(this.websiteTarget.value) : ""
    const rejectsDomain = domain !== "" && (logoSource === "domain" || logoSource === "")

    this.removedHintTarget.textContent = rejectsDomain ? hintDomain.replace("%{domain}", domain) : hintName
  }

  #setAccepted(accepted) {
    if (this.hasAcceptedFlagTarget) this.acceptedFlagTarget.value = accepted ? "1" : "0"
  }

  #hideOffer() {
    if (this.hasOfferNoticeTarget) this.offerNoticeTarget.classList.add("d-none")
  }

  // Persisted (logo_rejected_key) or staged in this session. Both count: the
  // modal is reopened with the persisted one, and a decline is staged until Save.
  #alreadyRefused(domain) {
    if (!domain || !this.hasDeclinedDomainTarget) return false

    const target = this.declinedDomainTarget
    return domain === (target.dataset.rejectedDomain || null) || domain === (target.value || null)
  }

  // Mirrors LogoService.extract_domain: the value produced here becomes a
  // "domain:<host>" rejection key on the server and is compared as a string, so
  // the two spellings of one DNS name have to fold the same way on both sides.
  #canonicalDomain(webAddress) {
    let host = webAddress.trim()
    try {
      host = new URL(host.startsWith("http") ? host : `http://${host}`).hostname
    } catch (_e) {
      // Not parseable as a URL — strip the same pieces by hand.
      host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").split(/[/?#]/)[0]
      host = host.split("@").pop().split(":")[0]
    }
    return host.toLowerCase().replace(/^www\./, "").replace(/\.$/, "")
  }

  // The caller has already set domainValue, so #beginLookup's url IS this
  // domain's — and going through it puts the offer under the same
  // request-identity guard as loadLogo, which it has to be: both are answers to
  // the same edit, so an older one arriving last must not win here either.
  async #fetchOffer(domain) {
    const request = this.#beginLookup()

    try {
      const response = await fetch(request.url, { signal: this.abortController.signal })
      if (!this.#isCurrentLookup(request)) return
      if (!response.ok) return

      const data = await response.json()
      if (!this.#isCurrentLookup(request)) return

      if (data.logo_url) this.displayOffer(data.logo_url, domain)
    } catch (_error) {
      // A failed offer is not worth surfacing — the user simply gets no offer.
      // An abort lands here too and is equally silent, by the same token.
    }
  }
}
